/**
 * Days — what a run produced, grouped by the moment it came from.
 *
 * The grouping IS the handoff: day, time, type is exactly the shape FluidTalk
 * wants, so an image arrives already knowing which part of her day it belongs
 * to rather than as one more file in a folder.
 */
import { useCallback, useEffect, useState } from 'react';
import { api, mediaUrl, downloadMedia, type DayBeat, type PhotoSet } from '../lib/api';

function Img({ src, alt, onClick }: { src: string | null; alt: string; onClick?: () => void }) {
  const [url, setUrl] = useState<string | null>(src?.startsWith('http') ? src : null);
  useEffect(() => {
    let live = true;
    if (!src) { setUrl(null); return; }
    // A FluidCloud link is just a URL — no token, no fetch, no object URL.
    if (src.startsWith('http')) { setUrl(src); return; }
    void mediaUrl(src).then((u) => { if (live) setUrl(u); }).catch(() => undefined);
    return () => { live = false; };
  }, [src]);
  if (!url) return <span className="ph" aria-label={alt} />;
  return <img src={url} alt={alt} onClick={onClick} />;
}

export default function Days({ characterId }: { characterId: string }) {
  const [sets, setSets] = useState<PhotoSet[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [beats, setBeats] = useState<DayBeat[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const r = await api.get<{ sets: PhotoSet[] }>('/sets');
        const hers = r.sets.filter((s) => s.characterId === characterId);
        setSets(hers);
        setOpenId((prev) => prev ?? hers[0]?.id ?? null);
      } catch (err) { setError(err instanceof Error ? err.message : 'Could not load her sets'); }
    })();
  }, [characterId]);

  const load = useCallback(async () => {
    if (!openId) { setBeats([]); return; }
    try {
      const r = await api.get<{ beats: DayBeat[] }>(`/sets/${openId}/renders`);
      setBeats(r.beats);
      setError(null);
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not load that day'); }
  }, [openId]);

  useEffect(() => { void load(); }, [load]);

  // Anything still queued or running is worth watching; nothing else is.
  const live = beats.some((b) => b.renders.some((r) => r.status === 'queued' || r.status === 'running'));
  useEffect(() => {
    if (!live) return;
    const t = setInterval(() => void load(), 3000);
    return () => clearInterval(t);
  }, [live, load]);

  const done = beats.reduce((n, b) => n + b.renders.filter((r) => r.status === 'done').length, 0);
  const withImages = beats.filter((b) => b.renders.length > 0);

  return (
    <div className="card">
      <div className="world-head">
        <div style={{ flex: 1 }}>
          <span className="eyebrow">Days</span>
          <p className="muted">
            What her sets produced, grouped by moment. Same room at 08:10 and 22:30 in different
            light — that is what a day buys you that seven separate renders never would.
          </p>
        </div>
        {sets.length > 0 && (
          <select className="field" style={{ maxWidth: 240 }} value={openId ?? ''} onChange={(e) => setOpenId(e.target.value)}>
            {sets.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        {done > 0 && <span className="pill ok"><span className="dot" />{done} done</span>}
      </div>

      {error && <div className="notice bad" style={{ marginBottom: 14 }}>{error}</div>}

      {sets.length === 0 && (
        <div className="notice">
          No sets for her yet. Build one in the Sets tab, then run it — the results land here.
        </div>
      )}

      {sets.length > 0 && withImages.length === 0 && (
        <div className="notice">
          This set has not been run yet. Open it in Sets and press Run — every beat lands here as its
          own group.
        </div>
      )}

      {withImages.map((b) => (
        <div key={b.beatId} className="day-beat">
          <div className="day-head">
            {b.timeLabel && (
              <span className="when">
                <span className="time">{b.timeLabel}</span>
                {b.dayNight && <span className="tod">{b.dayNight}</span>}
              </span>
            )}
            <span className="nm">{b.name || 'Untitled beat'}</span>
            {b.placeName && <span className="chip place">{b.placeName}</span>}
            <span className="chip mode">{b.camera}</span>
            <span className="chip">{b.outfitSlot || '—'}</span>
            <span className="faint mono day-light">
              {b.camera === 'no-face' ? 'no identity sent · cannot drift' : b.light}
            </span>
          </div>
          <div className="day-shots">
            {b.renders.map((r) => (
              <div key={r.id} className={`day-shot${r.status === 'failed' ? ' failed' : ''}`}>
                {r.status === 'done' && r.src
                  ? <Img src={r.src} alt={b.name} />
                  : r.status === 'failed'
                    ? <span className="ph bad" title={r.error ?? ''}>failed</span>
                    : <span className="ph"><span className="spin" /></span>}
                {r.tier !== 'plain' && <span className={`tier t-${r.tier}`}>{r.tier}</span>}
                {r.status === 'done' && r.src && (
                  <div className="over">
                    <button
                      className="btn btn-sm"
                      onClick={() => void downloadMedia(r.src!, `${b.name || 'shot'}-${r.id.slice(0, 6)}`)}
                    >Save</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
